import React, { useState, useRef, useEffect } from "react";
import { Card, Avatar, Divider, Flex, Typography } from "antd";

/* ICONS */
import { ReactComponent as NightMoonIcon } from "../assets/images/nightMoon.svg";
import { ReactComponent as HotelBellIconBlack } from "../assets/images/hotel-reception-bell.svg";
import IceCream from "../assets/images/iceCream.svg";
import { ReactComponent as HotelBellIcon } from "../assets/images/hotelBell.svg";
import { ReactComponent as SpoonIcon } from "../assets/images/spoonIcon.svg";
import FlightDetailsTable from "./FlightDetailsTable";

const { Title, Paragraph, Text } = Typography;
const BACKEND_URL = process.env.REACT_APP_API_URL;

const ItineraryData = ({ itineraryData }) => {
  const days = itineraryData?.Days || [];

  const [selectedDay, setSelectedDay] = useState(null);
  const [activeTab, setActiveTab] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = useRef(null);
  const [isTabOpen, setIsTabOpen] = useState(false);
  const [isContentExpanded, setIsContentExpanded] = useState(false);



  useEffect(() => {
    if (days.length) {
      setSelectedDay(days[0].id);
    }
  }, [days]);

  const day = days.find((d) => d.id === selectedDay);

  // ---------- OVERFLOW CHECK ----------
  useEffect(() => {
    const checkOverflow = () => {
      if (contentRef.current) {
        setIsOverflowing(
          contentRef.current.scrollHeight >
          contentRef.current.clientHeight
        );
      }
    };

    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [day, activeTab, isExpanded]);

  const formatChildren = (children = []) =>
    children
      .map((c) => {
        // LINK NODE
        if (c.type === "link") {
          const text = (c.children || [])
            .map((child) => child.text || "")
            .join("");

          return `<a href="${c.url}" target="_blank" rel="noopener noreferrer" class="text-primary font-medium !no-underline">${text}</a>`;
        }

        // TEXT NODE
        let t = c.text || "";

        if (c.bold) t = `<strong>${t}</strong>`;
        if (c.italic) t = `<em>${t}</em>`;
        if (c.underline) t = `<u>${t}</u>`;

        return t;
      })
      .join("");

  const renderBlocks = (blocks = []) =>
    blocks.map((b, i) => {
      const html = formatChildren(b.children);

      // Check if this block contains a table
      const hasTable = html.includes("<table");

      if (b.type === "heading") {
        const Tag = `h${b.level}`;
        return (
          <Tag
            key={i}
            className="font-semibold mb-3"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        );
      }
      if (b.type === "list") {
        const items = b.children
          .map((li) => `<li>${formatChildren(li.children)}</li>`)
          .join("");
        return (
          <ul
            key={i}
            className="list-disc pl-6 mb-3 text-sm"
            dangerouslySetInnerHTML={{ __html: items }}
          />
        );
      }

      // =========================
      // TABLE CONTENT
      // =========================
      if (hasTable) {
        return (
          <div
            key={i}
            className="
            table-wrapper
            mb-5
            overflow-x-auto
            [&_table]:w-full
            [&_table]:border-collapse
            [&_th]:border
            [&_th]:border-gray-300
            [&_th]:bg-gray-100
            [&_th]:p-3
            [&_th]:text-left
            [&_td]:border
            [&_td]:border-gray-300
            [&_td]:p-3
          "
            dangerouslySetInnerHTML={{ __html: html }}
          />
        );
      }
      return (
        <p
          key={i}
          className="mb-3 text-sm"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      );
    });

  const buildTabs = () => {
    if (!day) return [];

    const tabs = [];

    Object.entries(day).forEach(([key, value]) => {
      if (
        [
          "id",
          "day_number",
          "title",
          "image",
          "activity",
          "your_itinerary",
          "Facilities"
        ].includes(key)
      )
        return;

      // ---------- CUSTOM SECTIONS ----------
      if (key === "sections" && Array.isArray(value)) {
        value.forEach((section, index) => {
          if (section?.content?.length) {
            tabs.push({
              key: `section-${index}`,
              label: section.title || "Section",
              content: section.content,
            });
          }
        });
        return;
      }

      // ---------- TOUR DETAILS ----------
      if (key === "tour_details" && Array.isArray(value) && value.length) {
        tabs.push({
          key,
          label: "Tour Details",
          content: value,
          expandable: true,
        });
        return;
      }

      // ---------- SECTION COMPONENTS ----------
      if (value?.content?.length) {
        tabs.push({
          key,
          label: key
            .replace(/_/g, " ")
            .replace(/\b\w/g, (l) => l.toUpperCase()),
          content: value.content,
        });
        return;
      }

      // ---------- REPEATABLE COMPONENTS ----------
      if (Array.isArray(value) && value.length) {
        tabs.push({
          key,
          label: key
            .replace(/_/g, " ")
            .replace(/\b\w/g, (l) => l.toUpperCase()),
          type: key,
          data: value,
        });
      }
    });

    return tabs;
  };
  const tabs = buildTabs();

 useEffect(() => {
  if (tabs.length && activeTab === null) {
    setActiveTab(tabs[0].key);
    setIsExpanded(false);
  }
}, [tabs]);

  if (!day) return null;



  return (
    <div className="flex mt-8 border-t bg-white text-sm antialiased">
      {/* LEFT PANEL */}
      {days.length > 1 && (
        <aside className="w-36 border-r border-gray-200">
          <div className="h-28 border-b border-gray-200 mb-8 flex items-center justify-center gap-2">
            <span className="text-[#C4BFBF] font-extrabold text-6xl">
              {days.length}
            </span>
            <div>
              <div>Days</div>
              <div className="text-danger">Plan</div>
            </div>
          </div>

          <div className="relative">
            <div className="absolute left-[-0.20rem] top-3 bottom-0 w-[2px] bg-gray-200 h-[85%]" />

            <div className="flex flex-col gap-6 ml-6">
              {days.map((d) => (
                <div
                  key={d.id}
                  onClick={() => setSelectedDay(d.id)}
                  className="relative flex items-center cursor-pointer"
                >
                  <div
                    className={`absolute -left-[32px] w-3 h-3 rounded-full ${selectedDay === d.id ? "bg-primary" : "bg-[#E3E7F0]"
                      }`}
                  />

                  <div
                    className={`px-4 py-2 rounded-md text-xs font-medium ${selectedDay === d.id
                      ? "bg-primary text-white"
                      : "bg-gray-100 text-gray-700"
                      }`}
                  >
                    Day {d.day_number}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </aside>
      )}

      {/* RIGHT CONTENT */}

      <main className={`flex-1 ${days.length <= 1 ? "w-full" : ""}`}>

        {/* ---------- DESKTOP TABS ---------- */}
        <div className="hidden md:flex gap-0 border-b px-4 mb-4 overflow-x-auto h-16">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
                setActiveTab(tab.key);
                setIsTabOpen(false);
              }}
              className={`text-sm whitespace-nowrap border-b-2 px-2 ${activeTab === tab.key
                ? "border-danger text-danger font-semibold"
                : "border-transparent font-medium text-gray-900"
                }`}
            >
              {tab.label}
            </button>
          ))}
        </div>


        {/* ---------- MOBILE ACCORDION ---------- */}
        <div className="md:hidden mb-4 md:px-4">
          {tabs.map((tab) => {
            const isOpen = activeTab === tab.key;

            return (
              <div
                key={tab.key}
                className="border-b border-gray-200"
              >
                {/* Accordion Header */}
                <button
                  type="button"
                  onClick={() => {
                    setActiveTab((prev) =>
                      prev === tab.key ? null : tab.key
                    );
                    setIsContentExpanded(false);
                  }}
                  className={`w-full flex items-center justify-between py-4 text-sm font-semibold text-left ${isOpen ? "text-danger" : "text-gray-900"
                    }`}
                >
                  <span>{tab.label}</span>

                  <span
                    className={`transition-transform text-primary duration-200 ${isOpen ? "rotate-180" : ""
                      }`}
                  >
                    ▼
                  </span>
                </button>

                {/* Accordion Content */}
                {isOpen && (
                  <div className="pb-4">
                    {tab.type === "hotel" ? (
                      <HotelBlock day={day} />
                    ) : (
                      <>
                        <div
                          ref={contentRef}
                          className={`overflow-hidden transition-all richText ${tab.expandable && !isContentExpanded
                              ? "max-h-[300px]"
                              : "max-h-none"
                            }`}
                        >
                          {renderBlocks(tab.content)}
                        </div>

                        {tab.expandable &&
                          (isOverflowing || isContentExpanded) && (
                            <button
                              type="button"
                              onClick={() =>
                                setIsContentExpanded((prev) => !prev)
                              }
                              className="text-primary mt-2 text-sm font-semibold"
                            >
                              {isContentExpanded
                                ? "Show less ▲"
                                : "See more ▼"}
                            </button>
                          )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>



        {/* ---------- CONTENT ---------- */}
        {/* <div className="px-4">
          {tabs.map((tab) => {
            if (tab.key !== activeTab) return null;

            if (tab.type === "hotel") {
              return <HotelBlock key={tab.key} day={day} />;
            }

            return (
              <div key={tab.key}>

                <div
                  ref={contentRef}
                  className={`overflow-hidden transition-all richText ${tab.expandable && !isContentExpanded
                    ? "max-h-[300px] tsets"
                    : "max-h-none"
                    }`}
                >
                  {renderBlocks(tab.content)}
                </div>

                {tab.expandable && (isOverflowing || isContentExpanded) && (
                  <button
                    type="button"
                    onClick={() =>
                      setIsContentExpanded((prev) => !prev)
                    }
                    className="text-primary mt-2 text-sm font-semibold px-4 mr-auto"
                  >
                    {isContentExpanded
                      ? "Show less ▲"
                      : "See more ▼"}
                  </button>
                )}

              </div>
            );
          })}
        </div> */}

{/* ---------- DESKTOP CONTENT ---------- */}
<div className="hidden md:block px-4">
  {tabs.map((tab) => {
    if (tab.key !== activeTab) return null;

    if (tab.type === "hotel") {
      return <HotelBlock key={tab.key} day={day} />;
    }

    return (
      <div key={tab.key}>
        <div
          ref={contentRef}
          className={`overflow-hidden transition-all richText ${
            tab.expandable && !isContentExpanded
              ? "max-h-[300px]"
              : "max-h-none"
          }`}
        >
          {renderBlocks(tab.content)}
        </div>

        {tab.expandable &&
          (isOverflowing || isContentExpanded) && (
            <button
              type="button"
              onClick={() =>
                setIsContentExpanded((prev) => !prev)
              }
              className="text-primary mt-2 text-sm font-semibold px-0 mr-auto"
            >
              {isContentExpanded
                ? "Show less ▲"
                : "See more ▼"}
            </button>
          )}
      </div>
    );
  })}
</div>

      </main>

    </div>
  );
};

/* ---------- HOTEL ---------- */
function HotelBlock({ day }) {
  if (!day.hotel_info?.length) return null;

  return (
    <div className="px-4">
      <Title level={5} className="!text-sm">
        <Avatar icon={<HotelBellIconBlack />} /> Comfortable Hotel
      </Title>

      <Divider className="mt-0" />

      {day.hotel_info.map((hotel) => (
        <Card key={hotel.id} className="rounded-xl mt-4">
          {hotel.hotel_image?.url && (
            <img
              src={`${BACKEND_URL}${hotel.hotel_image.url}`}
              alt=""
              className="w-full h-48 object-cover rounded"
            />
          )}

          <div className="p-3">
            <Text strong>{hotel.hotel_name}</Text>

            <Flex gap={20} className="mt-2">
              <Flex vertical align="center">
                <NightMoonIcon />
                <Paragraph>{hotel.nights || 0}N</Paragraph>
              </Flex>

              <MealStatus
                included={hotel.breakfast_included}
                icon={<IceCream />}
              />
              <MealStatus
                included={hotel.lunch_included}
                icon={<HotelBellIcon />}
              />
              <MealStatus
                included={hotel.dinner_included}
                icon={<SpoonIcon />}
              />
            </Flex>
          </div>
        </Card>
      ))}
    </div>
  );
}

function MealStatus({ included, icon }) {
  return (
    <Flex align="center" gap={4}>
      <Avatar
        style={{ backgroundColor: "transparent" }}
        icon={icon}
        className={included ? "text-green-500" : "text-red-500"}
      />
      <Text className={included ? "text-green-500" : "text-red-500"}>
        {included ? "Included ✓" : "Not Included ✗"}
      </Text>
    </Flex>
  );
}

export default ItineraryData;
